/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-useless-catch */
import {getCookie} from 'cookies-next/client';
import {jwtDecode} from 'jwt-decode';
import {deleteCookie} from 'cookies-next';
import {ENDPOINTS, METHODS} from './api';
import {COOKIES_FIELDS} from './constants';

export const deleteTokensFromCookies = () => {
  deleteCookie(COOKIES_FIELDS.token);
  deleteCookie(COOKIES_FIELDS.refreshToken);
};

export const setTokensToCookies = async ({
  token,
  refreshToken
}: {
  token: string;
  refreshToken: string;
}) => {
  try {
    const response = await fetch(ENDPOINTS.nextApi.setToken, {
      method: METHODS.post,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({token, refreshToken})
    });

    if (!response.ok) {
      throw new Error(`Failed to set tokens: ${response.status}`);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const checkUserRole = () => {
  const token = getCookie(COOKIES_FIELDS.token);
  if (!token) return null;

  try {
    const decoded = jwtDecode(token) as any;

    // Check if token has required fields
    if (!decoded || typeof decoded !== 'object') {
      // Only delete access token, keep refresh token for refresh attempt
      deleteCookie(COOKIES_FIELDS.token);
      return null;
    }

    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      // Token expired - but don't delete refresh token, we need it for refresh
      // Only delete the expired access token
      deleteCookie(COOKIES_FIELDS.token);
      return null;
    }

    // Check if token has entity_type
    if (!decoded.entity_type) {
      // Only delete access token, keep refresh token for refresh attempt
      deleteCookie(COOKIES_FIELDS.token);
      return null;
    }

    return decoded.entity_type;
  } catch (error) {
    // Only delete access token, keep refresh token for refresh attempt
    deleteCookie(COOKIES_FIELDS.token);
    return null;
  }
};

export const getTokensFromCookies = () => {
  const token = getCookie(COOKIES_FIELDS.token);
  const refreshToken = getCookie(COOKIES_FIELDS.refreshToken);
  return {token, refreshToken};
};

export const isTokenValid = () => {
  const token = getCookie(COOKIES_FIELDS.token);
  if (!token) return false;

  try {
    const decoded = jwtDecode(token) as any;

    // Check if token has required fields
    if (!decoded || typeof decoded !== 'object') {
      return false;
    }

    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      return false;
    }

    // Check if token has entity_type
    if (!decoded.entity_type) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const validateTokenAndCleanup = () => {
  const token = getCookie(COOKIES_FIELDS.token);
  if (!token) {
    return {isValid: false, role: null};
  }

  try {
    const decoded = jwtDecode(token) as any;

    // Check if token has required fields
    if (!decoded || typeof decoded !== 'object') {
      // Only delete access token, keep refresh token for refresh attempt
      deleteCookie(COOKIES_FIELDS.token);
      return {isValid: false, role: null};
    }

    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      // Token expired - but don't delete refresh token, we need it for refresh
      // Only delete the expired access token
      deleteCookie(COOKIES_FIELDS.token);
      return {isValid: false, role: null};
    }

    // Check if token has entity_type
    if (!decoded.entity_type) {
      // Only delete access token, keep refresh token for refresh attempt
      deleteCookie(COOKIES_FIELDS.token);
      return {isValid: false, role: null};
    }

    return {isValid: true, role: decoded.entity_type};
  } catch (error) {
    // Only delete access token, keep refresh token for refresh attempt
    deleteCookie(COOKIES_FIELDS.token);
    return {isValid: false, role: null};
  }
};

export const getUserIdFromToken = (): number | null => {
  const token = getCookie(COOKIES_FIELDS.token);
  if (!token) return null;

  try {
    const decoded = jwtDecode(token) as any;

    // Check if token has required fields
    if (!decoded || typeof decoded !== 'object') {
      return null;
    }

    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      return null;
    }

    // Try common JWT fields for user ID
    const userId =
      decoded.sub || decoded.id || decoded.user_id || decoded.userId;

    if (!userId) return null;

    // Return as number if it's a number string, otherwise return the value
    return typeof userId === 'number' ? userId : parseInt(userId, 10) || null;
  } catch (error) {
    return null;
  }
};
